import React from 'react';
import { EntityId } from "@reduxjs/toolkit";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { destroyServer } from "src/actions/mine_craft/serversActions";
import { selectServerEntities, selectServerIds, setEditId } from "src/store/mine_craft/serversSlice";
import usePermissions from "src/hooks/usePermissions";

import { message, Spin } from "antd";
import { LikeOutlined, MessageOutlined } from "@ant-design/icons";
import CardImage from "src/componets/CardImage";
import Pagination from 'src/componets/Pagination';
import IconText from "src/componets/IconText";
import { ButtonDestroy, ButtonEdit } from "src/componets/Buttons";

interface propsInterface {
  limit: number;
}

const _List = ({ limit }: propsInterface) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const ids = useAppSelector(selectServerIds);
  const items = useAppSelector(selectServerEntities);
  const count = useAppSelector(state => state.mineCraft.servers.count);
  const error = useAppSelector(state => state.mineCraft.servers.error);
  const status = useAppSelector(state => state.mineCraft.servers.status);
  const { write_minecraft } = usePermissions();

  function onClickEdit(id: EntityId) {
    dispatch(setEditId(id));
  }

  function onClickDestroy(id: EntityId) {
    handleDestroy(id).catch(console.error);
  }

  async function handleDestroy (id: any) {
    const resultAction = await dispatch(destroyServer(id));

    if (destroyServer.fulfilled.match(resultAction)) {
      message.success(`ItemID ${id} was successful deleted`);
      setTimeout(() => navigate(0), 1000);
    }

    if (destroyServer.rejected.match(resultAction)) {
      message.error(resultAction.error.message);
    }
  }

  const renderList = (id: EntityId) => {
    const item = items[id];

    if (!item) {
      return;
    }

    const ribbon: any = item.kind === 'official'
      ? { color: 'gold', placement: 'start', text: 'official' }
      : { color: 'cyan', placement: 'start', text: 'user' };

    if (!item.published) {
      ribbon.color = '#b3b3b3';
      ribbon.text  = 'unpublished';
    }

    // Provisioning state trumps the rest — an installing/failed hosted
    // server is the thing the admin needs to see at a glance.
    if (item.hosted_server?.state === 'installing') {
      ribbon.color = 'blue';
      ribbon.text  = 'installing';
    } else if (item.hosted_server?.state === 'failed') {
      ribbon.color = 'red';
      ribbon.text  = 'failed';
    }

    return (
      <CardImage
        key={item.id}
        size='medium'
        className='v-server-card'
        image={item.image_url}
        title={item.title}
        link={item.url}
        ribbon={ribbon}
        stats={[
          <IconText icon={LikeOutlined} text={item.likes_count} />,
          <IconText icon={MessageOutlined} text={item.comments_count} />,
        ]}
        options={write_minecraft ? [
          <ButtonEdit key={`edit-${item.id}`} onClick={() => onClickEdit(item.id)} />,
          <ButtonDestroy key={`destroy-${item.id}`} onClick={() => onClickDestroy(item.id)} />
        ] : []}
      >
        <div className='v-small_text'>
          <small>{item.ip}:{item.port}</small>
        </div>
      </CardImage>
    )
  }

  if (status === 'loading') {
    return <Spin/>;
  }

  if (status === 'failed') {
    return <div>Error: {error}</div>
  }

  return (
    <>
      <div className='v-servers-list'>
        {ids && ids.map(renderList)}
      </div>

      <Pagination total={count} size={limit} />
    </>
  );
}

export default _List;
